import React, { useState } from 'react';
import { HiOutlineLightBulb, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';

const Step5_Recommendations = ({ incidentData, readOnly = false }) => {
  const [recommendations, setRecommendations] = useState(incidentData.recommendations || []);

  const [newRec, setNewRec] = useState({
    action: '',
    assignedTo: '',
    dueDate: '',
    status: 'Pending'
  });

  if (!incidentData) return null;

  const handleAdd = () => {
    if (!newRec.action || !newRec.assignedTo || !newRec.dueDate) return;
    setRecommendations([...recommendations, { ...newRec, id: Date.now() }]);
    setNewRec({ action: '', assignedTo: '', dueDate: '', status: 'Pending' });
  };

  const handleRemove = (id) => {
    setRecommendations(recommendations.filter(r => r.id !== id && r.action !== id)); // simple fallback for mock
  };

  return (
    <div className="bg-card p-6 rounded-xl border border-gray-200 shadow-sm max-w-4xl mx-auto my-0">
      <div className="border-b border-gray-200 pb-4 mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-md font-bold text-primary flex items-center gap-2">
            <HiOutlineLightBulb className="w-6 h-6 text-yellow-500" />
            Recommendations (CAPA)
          </h2>
          <p className="text-tertiary text-sm mt-1">Suggest Corrective and Preventative Actions to avoid recurrence of this incident.</p>
        </div>
      </div>

      <div className="space-y-6">

        {/* Add New Recommendation */}
        {!readOnly && (
          <div className="bg-page-bg p-4 rounded-xl border border-gray-200">
            <h3 className="text-base font-semibold text-primary mb-3">Add New Action</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-tertiary mb-1">Action Description</label>
                <input
                  type="text"
                  className="w-full rounded-md p-2 border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={newRec.action}
                  onChange={(e) => setNewRec({ ...newRec, action: e.target.value })}
                  placeholder="e.g. Install safety guard..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-tertiary mb-1">Assigned Department / User</label>
                <input
                  type="text"
                  className="w-full rounded-md p-2 border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                  value={newRec.assignedTo}
                  onChange={(e) => setNewRec({ ...newRec, assignedTo: e.target.value })}
                  placeholder="e.g. Maintenance"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-tertiary mb-1">Target Date</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    className="w-full p-2 rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-base bg-card"
                    value={newRec.dueDate}
                    onChange={(e) => setNewRec({ ...newRec, dueDate: e.target.value })}
                  />
                  <button
                    onClick={handleAdd}
                    disabled={!newRec.action || !newRec.assignedTo || !newRec.dueDate}
                    className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-md transition-colors"
                  >
                    <HiOutlinePlus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Recommendations List */}
        <div>
          <h3 className="text-sm   font-semibold text-secondary uppercase tracking-wider mb-3">Proposed Actions ({recommendations.length})</h3>

          {recommendations.length === 0 ? (
            <div className="text-center p-6 border-2 border-dashed border-gray-200 rounded-lg text-tertiary bg-page-bg">
              No recommendations added yet.
            </div>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-page-bg">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-base font-semibold text-primary sm:pl-6">Action</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-primary">Assigned To</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-primary">Target Date</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-base font-semibold text-primary">Status</th>
                    {!readOnly && <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Remove</span></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-card">
                  {recommendations.map((rec, idx) => (
                    <tr key={rec.id || idx}>
                      <td className="whitespace-normal py-4 pl-4 pr-3 text-base font-medium text-primary sm:pl-6">
                        {rec.action}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-base text-tertiary">
                        {rec.assignedTo}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-base text-tertiary">
                        {new Date(rec.dueDate).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-base text-tertiary">
                        <span className={`px-2 inline-flex text-base leading-5 font-semibold rounded-full 
                          ${rec.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {rec.status}
                        </span>
                      </td>
                      {!readOnly && (
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-base font-medium sm:pr-6">
                          <button
                            onClick={() => handleRemove(rec.id || rec.action)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          >
                            <HiOutlineTrash className="w-5 h-5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Step5_Recommendations;
